import React, { useEffect, useState } from "react";
import axios from "axios";
import { Link, useSearchParams } from "react-router-dom";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { ArrowLeft, Loader2, Mail, Send, Check, MessageCircle, ChevronDown, Lightbulb } from "lucide-react";
import { ColyAvatar } from "@/components/ColyMascot";
import Wordmark from "@/components/Wordmark";

const SUBJECT_OPTIONS = [
  "General question",
  "Bug report / Something's broken",
  "Feature request / Idea",
  "Account or sign-up help",
  "Press / Media inquiry",
  "Partnership / Collaboration",
  "Privacy or data question",
];
const OTHER = "Other";
const FEATURE_OPTION = "Feature request / Idea";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const Support = () => {
  const [searchParams] = useSearchParams();
  const isFeatureMode = searchParams.get("topic") === "feature";

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const [form, setForm] = useState({
    name: "",
    email: "",
    subject: isFeatureMode ? FEATURE_OPTION : "",
    message: "",
  });
  const [subjectChoice, setSubjectChoice] = useState(
    isFeatureMode ? FEATURE_OPTION : ""
  );
  const [otherSubject, setOtherSubject] = useState("");
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);

  const update = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const handleSubjectChange = (e) => {
    const value = e.target.value;
    setSubjectChoice(value);
    if (value === OTHER) {
      setForm((f) => ({ ...f, subject: otherSubject }));
    } else {
      setForm((f) => ({ ...f, subject: value }));
    }
  };

  const handleOtherSubjectChange = (e) => {
    const value = e.target.value;
    setOtherSubject(value);
    setForm((f) => ({ ...f, subject: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.email.trim() || !form.subject.trim() || !form.message.trim()) {
      toast.error("Please fill in all fields");
      return;
    }
    setLoading(true);
    try {
      await axios.post(`${API}/support`, form);
      setDone(true);
      toast.success("Message sent!", { description: "We'll get back to you soon." });
    } catch (err) {
      const detail = err?.response?.data?.detail;
      if (Array.isArray(detail) && detail[0]?.msg) {
        toast.error("Check your details", { description: detail[0].msg });
      } else {
        toast.error("Something went wrong", { description: "Please try again in a moment." });
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-coly-cream text-coly-ink">
      <header className="border-b border-coly-ink/10 bg-coly-cream/95 backdrop-blur-md sticky top-0 z-50">
        <div className="max-w-3xl mx-auto px-5 sm:px-6 py-4 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2.5 group" data-testid="support-home-link">
            <ColyAvatar size={32} />
            <Wordmark height={26} color="#3F7556" />
          </Link>
          <Link
            to="/"
            className="inline-flex items-center gap-1.5 text-sm font-extrabold text-coly-ink-soft hover:text-coly-green-deep transition-colors"
            data-testid="support-back"
          >
            <ArrowLeft className="w-4 h-4" />
            Back to home
          </Link>
        </div>
      </header>

      <main className="max-w-3xl mx-auto px-5 sm:px-6 py-10 sm:py-14" data-testid="support-page">
        <div className="flex flex-col items-center text-center mb-10">
          <motion.div
            initial={{ scale: 0.6, opacity: 0, rotate: -8 }}
            animate={{ scale: 1, opacity: 1, rotate: 0 }}
            transition={{ type: "spring", stiffness: 220, damping: 14 }}
            className="mb-4"
          >
            <ColyAvatar size={96} />
          </motion.div>
          <span className={`inline-flex items-center gap-1.5 bg-white border border-coly-ink/10 rounded-full px-3 py-1 text-[11px] font-extrabold uppercase tracking-wider mb-4 ${isFeatureMode ? "text-coly-yellow-deep" : "text-coly-green-deep"}`}>
            {isFeatureMode ? <Lightbulb className="w-3 h-3" /> : <MessageCircle className="w-3 h-3" />}
            {isFeatureMode ? "Feature suggestion" : "Support"}
          </span>
          {isFeatureMode ? (
            <>
              <h1 className="text-[32px] sm:text-[44px] font-black tracking-tight leading-[1.05] m-0" data-testid="support-feature-heading">
                Got an idea? <span className="text-coly-green-deep">We&apos;re all ears.</span>
              </h1>
              <p className="mt-4 text-coly-ink-soft text-[15px] sm:text-[17px] max-w-xl leading-relaxed font-semibold">
                If you feel a feature needs to be in the app, or something is missing —
                let us know under here. Every idea from a real gut warrior shapes what we build next.
              </p>
            </>
          ) : (
            <>
              <h1 className="text-[32px] sm:text-[44px] font-black tracking-tight leading-[1.05] m-0">
                Got a question? <span className="text-coly-green-deep">We&apos;re listening.</span>
              </h1>
              <p className="mt-4 text-coly-ink-soft text-[15px] sm:text-[17px] max-w-xl leading-relaxed font-semibold">
                Bug report, feature idea, feedback on the app, or just want to say hi —
                drop us a note and we&apos;ll get back to you as soon as we can.
              </p>
            </>
          )}
          <a
            href="mailto:support@colyapp.com"
            className="mt-5 inline-flex items-center gap-2 text-sm font-extrabold text-coly-ink-soft hover:text-coly-green-deep transition-colors"
            data-testid="support-email-link"
          >
            <Mail className="w-4 h-4" />
            support@colyapp.com
          </a>
        </div>

        {done ? (
          <motion.div
            initial={{ scale: 0.96, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            transition={{ type: "spring", stiffness: 220, damping: 16 }}
            className="bg-white rounded-3xl border border-coly-ink/10 p-8 sm:p-10 text-center"
            style={{ boxShadow: "0 30px 80px -40px rgba(47,58,44,0.3)" }}
            data-testid="support-success"
          >
            <div className="w-16 h-16 rounded-full bg-coly-green text-white flex items-center justify-center mx-auto mb-4">
              <Check className="w-7 h-7" strokeWidth={3} />
            </div>
            <h2 className="text-[24px] font-black tracking-tight m-0">Message sent!</h2>
            <p className="mt-2 text-coly-ink-soft font-semibold">
              Thanks for reaching out, {form.name.split(" ")[0] || "friend"}. We&apos;ll reply to{" "}
              <span className="font-extrabold text-coly-ink">{form.email}</span> soon.
            </p>
            <button
              type="button"
              onClick={() => {
                setForm({ name: "", email: "", subject: "", message: "" });
                setSubjectChoice("");
                setOtherSubject("");
                setDone(false);
              }}
              className="mt-6 inline-flex items-center gap-2 bg-coly-cream2 hover:bg-coly-cream text-coly-ink rounded-full px-5 py-2.5 font-extrabold text-sm border border-coly-ink/10"
              data-testid="support-send-another"
            >
              Send another message
            </button>
          </motion.div>
        ) : (
          <form
            onSubmit={handleSubmit}
            className="bg-white rounded-3xl border border-coly-ink/10 p-6 sm:p-8 space-y-5"
            style={{ boxShadow: "0 30px 80px -40px rgba(47,58,44,0.3)" }}
            data-testid="support-form"
          >
            <div className="grid sm:grid-cols-2 gap-5">
              <div>
                <label className="block text-[12px] font-extrabold uppercase tracking-wider text-coly-ink-soft mb-1.5">
                  Your name
                </label>
                <input
                  type="text"
                  value={form.name}
                  onChange={update("name")}
                  placeholder="Alex"
                  required
                  maxLength={120}
                  className="w-full bg-coly-cream border-2 border-coly-ink/10 rounded-2xl px-4 py-3 outline-none font-semibold text-coly-ink focus:border-coly-green transition-colors"
                  data-testid="support-name-input"
                />
              </div>
              <div>
                <label className="block text-[12px] font-extrabold uppercase tracking-wider text-coly-ink-soft mb-1.5">
                  Email
                </label>
                <input
                  type="email"
                  value={form.email}
                  onChange={update("email")}
                  placeholder="you@example.com"
                  required
                  className="w-full bg-coly-cream border-2 border-coly-ink/10 rounded-2xl px-4 py-3 outline-none font-semibold text-coly-ink focus:border-coly-green transition-colors"
                  data-testid="support-email-input"
                />
              </div>
            </div>

            <div>
              <label className="block text-[12px] font-extrabold uppercase tracking-wider text-coly-ink-soft mb-1.5">
                Subject
              </label>
              <div className="relative">
                <select
                  value={subjectChoice}
                  onChange={handleSubjectChange}
                  required
                  className="w-full appearance-none bg-coly-cream border-2 border-coly-ink/10 rounded-2xl pl-4 pr-10 py-3 outline-none font-semibold text-coly-ink focus:border-coly-green transition-colors cursor-pointer"
                  data-testid="support-subject-select"
                >
                  <option value="" disabled>
                    Choose a topic…
                  </option>
                  {SUBJECT_OPTIONS.map((opt) => (
                    <option key={opt} value={opt}>
                      {opt}
                    </option>
                  ))}
                  <option value={OTHER}>Other…</option>
                </select>
                <ChevronDown className="absolute right-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-coly-ink-soft pointer-events-none" />
              </div>
              {subjectChoice === OTHER && (
                <motion.input
                  initial={{ opacity: 0, y: -6 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.18 }}
                  type="text"
                  value={otherSubject}
                  onChange={handleOtherSubjectChange}
                  placeholder="Tell us what it's about…"
                  required
                  autoFocus
                  maxLength={160}
                  className="mt-2.5 w-full bg-coly-cream border-2 border-coly-ink/10 rounded-2xl px-4 py-3 outline-none font-semibold text-coly-ink focus:border-coly-green transition-colors"
                  data-testid="support-subject-other-input"
                />
              )}
            </div>

            <div>
              <label className="block text-[12px] font-extrabold uppercase tracking-wider text-coly-ink-soft mb-1.5">
                Message
              </label>
              <textarea
                value={form.message}
                onChange={update("message")}
                placeholder={isFeatureMode ? "Describe the feature you'd love to see…" : "Tell us what's on your mind…"}
                required
                rows={6}
                maxLength={5000}
                className="w-full bg-coly-cream border-2 border-coly-ink/10 rounded-2xl px-4 py-3 outline-none font-semibold text-coly-ink focus:border-coly-green transition-colors resize-none"
                data-testid="support-message-input"
              />
              <div className="mt-1.5 text-right text-[11px] font-bold text-coly-ink-soft">
                {form.message.length}/5000
              </div>
            </div>

            <motion.button
              whileTap={{ scale: 0.98 }}
              whileHover={{ y: -1 }}
              type="submit"
              disabled={loading}
              className="w-full bg-coly-green text-white rounded-full py-3.5 font-extrabold text-[15px] inline-flex items-center justify-center gap-2 disabled:opacity-70"
              style={{ boxShadow: "0 4px 0 #4F8E6A" }}
              data-testid="support-submit-btn"
            >
              {loading ? (
                <>
                  <Loader2 className="w-4 h-4 animate-spin" />
                  Sending…
                </>
              ) : (
                <>
                  <Send className="w-4 h-4" />
                  Send message
                </>
              )}
            </motion.button>

            <p className="text-center text-[12px] text-coly-ink-soft font-semibold">
              By contacting us you agree to our{" "}
              <Link to="/legal/privacy" className="font-extrabold text-coly-green-deep underline underline-offset-4">
                Privacy Policy
              </Link>
              .
            </p>
          </form>
        )}
      </main>
    </div>
  );
};

export default Support;
